import ObjectHash from 'object-hash'

export const state = () => {
	return {
		statsGlobal: {},
		ttl_statsGlobal: {},
		cache_key_statsGlobal: {},

		statsRepartition: {},
		ttl_statsRepartition: {},
		cache_key_statsRepartition: {},

		statsRetention: {},
		ttl_statsRetention: {},
		cache_key_statsRetention: {},

		statsPurchase: {},
		ttl_statsPurchase: {},
		cache_key_statsPurchase: {},

		statsBehaviour: {},
		ttl_statsBehaviour: {},
		cache_key_statsBehaviour: {},

		cache_duration: 10800000, // 3 hours
	}
}

function isInCacheStatsGlobal(state, statKey, companyId, params) {
	if (!state.ttl_statsGlobal[statKey] || state.ttl_statsGlobal[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsGlobal(statKey, companyId, params)
	return state.cache_key_statsGlobal[statKey] === cacheKey
}

function buildCacheKeyStatsGlobal(statKey, companyId, params) {
	return `company:${companyId}:global::params:${ObjectHash(params)}:${statKey}`
}

function isInCacheStatsRepartition(state, statKey, companyId, params) {
	if (!state.ttl_statsRepartition[statKey] || state.ttl_statsRepartition[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsRepartition(statKey, companyId, params)
	return state.cache_key_statsRepartition[statKey] === cacheKey
}

function buildCacheKeyStatsRepartition(statKey, companyId, params) {
	return `company:${companyId}:repartition::params:${ObjectHash(params)}:${statKey}`
}

function isInCacheStatsRetention(state, statKey, companyId, params) {
	if (!state.ttl_statsRetention[statKey] || state.ttl_statsRetention[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsRetention(statKey, companyId, params)
	return state.cache_key_statsRetention[statKey] === cacheKey
}

function buildCacheKeyStatsRetention(statKey, companyId, params) {
	return `company:${companyId}:retention::params:${ObjectHash(params)}:${statKey}`
}

function isInCacheStatsPurchase(state, statKey, companyId, params) {
	if (!state.ttl_statsPurchase[statKey] || state.ttl_statsPurchase[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsPurchase(statKey, companyId, params)
	return state.cache_key_statsPurchase[statKey] === cacheKey
}

function buildCacheKeyStatsPurchase(statKey, companyId, params) {
	return `company:${companyId}:purchase::params:${ObjectHash(params)}:${statKey}`
}

function isInCacheStatsBehaviour(state, statKey, companyId) {
	if (!state.ttl_statsBehaviour[statKey] || state.ttl_statsBehaviour[statKey] < Date.now()) {
		return false
	}

	const cacheKey = buildCacheKeyStatsBehaviour(statKey, companyId)
	return state.cache_key_statsBehaviour[statKey] === cacheKey
}

function buildCacheKeyStatsBehaviour(statKey, companyId) {
	return `company:${companyId}:behaviour::params::${statKey}`
}

export const actions = {
	async retrieveGlobalIndicators({ dispatch }, { companyId, params, evolution }) {
		const statKey = evolution ? 'global_evolution' : 'global'
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/global/`,
			statKey,
			companyId,
			params,
		})
	},

	async retrieveRevenueBySegment({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatGlobal', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/revenue-by-segment`,
			statKey: 'revenue-by-segment',
			companyId,
			params,
		})
	},

	async retrieveStatGlobal({ state, commit }, { endpoint, statKey, companyId, params }) {
		if (isInCacheStatsGlobal(state, statKey, companyId, params)) {
			return state.statsGlobal[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params })
			.then((data) => {
				commit('setStatsGlobal', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCacheGlobal', { statKey })
				throw err
			})
	},

	async retrieveDistribution({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatRepartition', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/distribution-by-type/`,
			statKey: 'distribution',
			companyId,
			params,
		})
	},

	async retrieveRepurchase({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatRepartition', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/repurchase-by-type/`,
			statKey: 'repurchase',
			companyId,
			params,
		})
	},

	async retrieveStatRepartition({ state, commit }, { endpoint, statKey, companyId, params }) {
		if (isInCacheStatsRepartition(state, statKey, companyId, params)) {
			return state.statsRepartition[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params })
			.then((data) => {
				commit('setStatsRepartition', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCacheRepartition', { statKey })
				throw err
			})
	},

	// possible statKey values: 'rate', 'clients', 'revenue, 'average-cart'
	async retrieveStatRetention({ state, commit }, { statKey, companyId, params }) {
		if (isInCacheStatsRetention(state, statKey, companyId, params)) {
			return state.statsRetention[statKey]
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/loyalty/orders/stats/retention-${statKey}/`, {
				params,
			})
			.then((data) => {
				const res = data.results
				commit('setStatsRetention', { statKey, data: res, companyId, params })
				return res
			})
			.catch((err) => {
				commit('clearCacheRetention', { statKey })
				throw err
			})
	},

	async retrievePurchaseIndicators({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatPurchase', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/repurchase-overview/`,
			statKey: 'indicators',
			companyId,
			params,
		})
	},

	async retrievePurchaseByDays({ dispatch }, { companyId, params }) {
		return await dispatch('retrieveStatPurchase', {
			endpoint: `api/company/${companyId}/loyalty/orders/stats/repurchase/`,
			statKey: 'repartition',
			companyId,
			params,
		})
	},

	async retrieveStatPurchase({ state, commit }, { endpoint, statKey, companyId, params }) {
		if (isInCacheStatsPurchase(state, statKey, companyId, params)) {
			return state.statsPurchase[statKey]
		}
		return await useNuxtApp()
			.$api(endpoint, { params })
			.then((data) => {
				commit('setStatsPurchase', { statKey, data, companyId, params })
				return data
			})
			.catch((err) => {
				commit('clearCachePurchase', { statKey })
				throw err
			})
	},

	async retrieveBehaviour({ state, commit }, { companyId }) {
		const statKey = 'factors'
		if (isInCacheStatsBehaviour(state, statKey, companyId)) {
			return state.statsBehaviour[statKey]
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/loyalty/orders/stats/behaviour/`)
			.then((data) => {
				commit('setStatsBehaviour', { statKey, data, companyId })
				return data
			})
			.catch((err) => {
				commit('clearCacheBehaviour', { statKey })
				throw err
			})
	},
}

export const mutations = {
	setStatsGlobal(state, { statKey, data, companyId, params }) {
		state.statsGlobal = { ...state.statsGlobal, [statKey]: data }

		state.ttl_statsGlobal[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsGlobal(statKey, companyId, params)
		state.cache_key_statsGlobal[statKey] = cacheKey
	},

	setStatsRepartition(state, { statKey, data, companyId, params }) {
		state.statsRepartition = { ...state.statsRepartition, [statKey]: data }

		state.ttl_statsRepartition[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsRepartition(statKey, companyId, params)
		state.cache_key_statsRepartition[statKey] = cacheKey
	},

	setStatsRetention(state, { statKey, data, companyId, params }) {
		state.statsRetention = { ...state.statsRetention, [statKey]: data }
		state.ttl_statsRetention[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsRetention(statKey, companyId, params)
		state.cache_key_statsRetention[statKey] = cacheKey
	},

	clearCacheGlobal(state, { statKey }) {
		state.statsGlobal[statKey] = null
		state.ttl_statsGlobal[statKey] = null
		state.cache_key_statsGlobal[statKey] = null
	},

	clearCacheRepartition(state, { statKey }) {
		state.statsRepartition[statKey] = null
		state.ttl_statsRepartition[statKey] = null
		state.cache_key_statsRepartition[statKey] = null
	},

	clearCacheRetention(state, { statKey }) {
		state.statsRetention[statKey] = null
		state.ttl_statsRetention[statKey] = null
		state.cache_key_statsRetention[statKey] = null
	},

	setStatsPurchase(state, { statKey, data, companyId, params }) {
		state.statsPurchase = { ...state.statsPurchase, [statKey]: data }

		state.ttl_statsPurchase[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsPurchase(statKey, companyId, params)
		state.cache_key_statsPurchase[statKey] = cacheKey
	},

	clearCachePurchase(state, { statKey }) {
		state.statsPurchase[statKey] = null
		state.ttl_statsPurchase[statKey] = null
		state.cache_key_statsPurchase[statKey] = null
	},

	setStatsBehaviour(state, { statKey, data, companyId }) {
		state.statsBehaviour = { ...state.statsBehaviour, [statKey]: data }

		state.ttl_statsBehaviour[statKey] = Date.now() + state.cache_duration

		const cacheKey = buildCacheKeyStatsBehaviour(statKey, companyId)
		state.cache_key_statsBehaviour[statKey] = cacheKey
	},

	clearCacheBehaviour(state, { statKey }) {
		state.statsBehaviour[statKey] = null
		state.ttl_statsBehaviour[statKey] = null
		state.cache_key_statsBehaviour[statKey] = null
	},
}
